//#region libraries
import React, { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";

// custom components
import { NewScreen, Title as CustomTitle } from "_atoms";
import { TableValue, TableHeader } from "_molecules";
import { ClickableTable } from "_organisms";

// bootstrap
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";

// exterior libraries
import { Toggle, ToggleItem } from "@tremor/react";

// redux
import { useSelector, useDispatch } from "react-redux";
import { selectFirebaseUser, selectUserDb, getDbInfo } from "_redux/slices/main-screen/user";
import { showAirport as setShowAirport, setAirport } from "_redux/slices/analytics/reviews";
import * as reviewsSlice from "_redux/slices/analytics/reviews";
import Pagination from "../survey-answers/Pagination";

// utils
import { lookupIsLoading } from "_utils/statusUtil";
//#endregion

const DEFAULT_LIMIT = 50;

const GlobalSla = (props) => {
  //#region states
  const history = useHistory();

  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [skip, setSkip] = useState(0);

  const dispatch = useDispatch();
  const dbUser = useSelector(selectUserDb);
  const firebaseUser = useSelector(selectFirebaseUser);

  const globalScores = useSelector(reviewsSlice.selectGlobalScores(search));
  const topAirports = useSelector(reviewsSlice.selectTopAirports(search));

  const globalScoresIsLoading = useSelector((state) =>
    lookupIsLoading(state.reviews.statusObj, [
      "getGlobalScores",
    ])
  );
  const topAirportsIsLoading = useSelector((state) =>
    lookupIsLoading(state.reviews.statusObj, [
      "getGlobalScores",
      "getTopAirportRankings",
    ])
  );
  //#endregion

  //#region use effects
  useEffect(() => {
    dispatch(reviewsSlice.reset());
  }, []);

  useEffect(() => {
    if (dbUser) {
      dispatch(reviewsSlice.getGlobalScores({
        departureAirport: dbUser.details.authority,
        startDate: "2020-01-01",
      }));
      dispatch(
        reviewsSlice.getTopAirportRanking({
          departureAirport: dbUser.details.authority,
          limit: 10000,
          skip: 0,
        })
      );
    } else if (firebaseUser?.uid) {
      dispatch(getDbInfo(firebaseUser.uid));
    }
  }, [dbUser, firebaseUser, dispatch]);

  useEffect(() => {
    setSkip(0);
  }, [tab, search]);
  //#endregion

  // columns
  const columns = useMemo(
    () => [
      {
        Header: <TableHeader title={"Ranking"} />,
        accessor: "ranking",
        Cell: ({ cell: { value } }) => <TableValue title={value} />,
      },
      {
        Header: <TableHeader title={"Airport"} />,
        accessor: "airport",
        Cell: ({ cell: { value } }) => <TableValue title={value} />,
      },
      {
        Header: <TableHeader title={"Quality"} />,
        accessor: "quality",
        Cell: ({ cell: { value } }) => (
          <TableValue title={value} stars={true} />
        ),
      },
      {
        Header: <TableHeader title={"Staff"} />,
        accessor: "staff",
        Cell: ({ cell: { value } }) => (
          <TableValue title={value} stars={true} />
        ),
      },
      {
        Header: <TableHeader title={"Waiting Time"} />,
        accessor: "waitingTime",
        Cell: ({ cell: { value } }) => (
          <TableValue title={value} stars={true} />
        ),
      },
      {
        Header: <TableHeader title={"Overall"} />,
        accessor: "overall",
        Cell: ({ cell: { value } }) => (
          <TableValue title={value} stars={true} overall={true} />
        ),
      },
      {
        Header: <TableHeader title={"Total Reviews"} />,
        accessor: "totalReviews",
        Cell: ({ cell: { value } }) => (
          <TableValue title={value} stars={false} overall={true} />
        ),
      },
      ...(tab == 0
        ? [
          {
            Header: <TableHeader title={"Total Requests"} />,
            accessor: "volume",
            Cell: ({ cell: { value } }) => (
              <TableValue title={value} stars={false} overall={true} />
            ),
          },
        ]
        : []),
    ],
    [tab]
  );

  const handleClick = (index) => {
    // expand profile view
    let airport = "";
    if (tab == 1) {
      airport = paginatedData[index];
    } else {
      airport = paginatedData[index];
    }
    dispatch(setAirport(airport));
    dispatch(setShowAirport(true));
    history.push("/airport-profile");
  };

  const activeData = tab == 1 ? globalScores : topAirports;
  const totalRows = activeData.length;
  const paginatedData =
    activeData.length > limit ? activeData.slice(skip, skip + limit) : activeData;
  const displayedData = paginatedData.map((airport, index) => ({
    ...airport,
    ranking: skip + index + 1,
  }));


  const showMore = () => {
    if (skip + limit < totalRows) {
      setSkip(skip + limit);
    }
  };

  const showLess = () => {
    if (skip - limit >= 0) {
      setSkip(skip - limit);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setSkip(0);
  };

  return (
    <>
      <NewScreen>
        <CustomTitle
          name="Feedback"
          subtitle={"Airport Analytics"}
          description={
            "View statistics on your relationship with global airports"
          }
        />

        {/* {showAirport ? (
          <AirportProfile airport={airport} />
        ) : ( */}
        <>
          {/* filters + search bar */}

          {/* table */}
          <Row>
            <>
              {(tab == 1 ? globalScoresIsLoading : topAirportsIsLoading) ? (
                <div style={{ "margin-top": "80px" }}>
                  <Row className="justify-content-center align-items-center">
                    {/* <CustomSpinner /> */}
                  </Row>
                </div>
              ) : (
                <ClickableTable
                  card={true}
                  headers={
                    <Col xs={3} style={{ marginLeft: 12 }}>
                      <Form.Control
                        onChange={(e) =>
                          setSearch(e.target.value.toUpperCase())
                        }
                        type="name"
                        placeholder="Search by IATA"
                        // defaultValue={search}
                        value={search}
                      />
                    </Col>
                  }
                  toggle={
                    <Col
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginRight: 12,
                      }}
                    >
                      <Toggle
                        color="zinc"
                        defaultValue={"0"}
                        onValueChange={(value) => setTab(value)}
                      >
                        <ToggleItem value={"0"} text="Top Airports" />
                        <ToggleItem value={"1"} text="Global Ranking" />
                      </Toggle>
                    </Col>
                  }
                  columns={columns}
                  data={displayedData}
                  handleClick={handleClick}
                  title={"Airport List"}
                  subtitle={
                    tab == 1
                      ? "Airports ranked by passenger review score"
                      : "Airports ranked by actual APP reviews submitted"
                  }
                />
              )}
            </>
          </Row>
          <Row className="mt-2">
            <div style={{ float: "inline-end", marginRight: "40px" }}>
              <Pagination
                showLess={showLess}
                showMore={showMore}
                limit={limit}
                skippedRows={skip}
                totalRows={totalRows}
                handleLimitChange={handleLimitChange}
              />
            </div>
          </Row>
        </>
        {/* )} */}
      </NewScreen>
    </>
  );
};

export default GlobalSla;
